import { useState, useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import { chatApi, type ApiChatResponse } from '../api/client'
import type { ChatMessage } from '../types'

interface UseChatOptions {
  onError?: (error: Error) => void
}

export function useChat(options?: UseChatOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [sessionId, setSessionId] = useState<string | undefined>()

  const mutation = useMutation({
    mutationFn: chatApi.sendMessage,
    onSuccess: (response: ApiChatResponse) => {
      setSessionId(response.sessionId)
      // Transform API response into ChatMessage
      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'ASSISTANT',
        content: response.message,
        timestamp: response.timestamp,
        toolCalls: response.toolsUsed?.map(tool => ({
          toolName: tool.toolName,
          input: {},
          output: tool.result
        }))
      }
      setMessages((prev) => [...prev, assistantMessage])
    },
    onError: (error: Error) => {
      options?.onError?.(error)
    },
  })

  const sendMessage = useCallback(
    async (content: string) => {
      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'USER',
        content,
        timestamp: new Date().toISOString(),
      }
      setMessages((prev) => [...prev, userMessage])

      await mutation.mutateAsync({
        message: content,
        sessionId,
      })
    },
    [mutation, sessionId]
  )

  const clearChat = useCallback(() => {
    setMessages([])
    setSessionId(undefined)
  }, [])

  return {
    messages,
    sessionId,
    sendMessage,
    clearChat,
    isLoading: mutation.isPending,
    error: mutation.error,
  }
}
